import React, { useState, useEffect, useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  ComposedChart,
  Area,
  AreaChart
} from 'recharts';
import { getOrgMonthlyOverview } from '../../../utils/functions';

export default function OrgMonthlyChart({ org, setOpenOrgModal }) {
  const [monthlyData, setMonthlyData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [monthsBack, setMonthsBack] = useState(12);
  const [chartType, setChartType] = useState('bar');
  const [selectedDoctors, setSelectedDoctors] = useState(new Set());
  const [sortBy, setSortBy] = useState('name'); // 'name', 'totalNotes', 'currentMonthNotes', 'avgNotes'
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc', 'desc'
  const [filterActiveOnly, setFilterActiveOnly] = useState(false);

  // Fetch data when org or monthsBack changes
  useEffect(() => {
    const fetchData = async () => {
      if (!org) return;

      setLoading(true);
      try {
        const data = await getOrgMonthlyOverview(org, monthsBack);
        setMonthlyData(data);
      } catch (error) {
        console.error('Error fetching monthly overview:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [org, monthsBack]);

  // Get current month (most recent month in data)
  const currentMonth = useMemo(() => {
    if (!monthlyData?.monthlyOverview || monthlyData.monthlyOverview.length === 0) return null;
    return monthlyData.monthlyOverview[0]; // First item is most recent
  }, [monthlyData]);

  // Get all unique doctors with their stats
  const allDoctorsWithStats = useMemo(() => {
    if (!monthlyData?.monthlyOverview) return [];

    const doctorMap = new Map();

    monthlyData.monthlyOverview.forEach(month => {
      month.activeDoctorsList.forEach(doctor => {
        if (!doctorMap.has(doctor.userId)) {
          doctorMap.set(doctor.userId, {
            userId: doctor.userId,
            userName: doctor.userName,
            userEmail: doctor.userEmail,
            totalNotes: 0,
            monthsActive: 0,
            currentMonthNotes: 0,
            lastActiveMonth: null // Add this field
          });
        }

        const doctorInfo = doctorMap.get(doctor.userId);
        
        // Add to total notes across all months
        const doctorStat = month.doctorStats.find(stat => stat.userId === doctor.userId);
        if (doctorStat && doctorStat.noteCount > 0) {
          doctorInfo.totalNotes += doctorStat.noteCount;
          doctorInfo.monthsActive += 1;

          // Track the most recent active month (data is ordered newest first)
          if (!doctorInfo.lastActiveMonth) {
            doctorInfo.lastActiveMonth = month.month;
          }

          // If this is the current month, store current month notes
          if (currentMonth && month.month === currentMonth.month) {
            doctorInfo.currentMonthNotes = doctorStat.noteCount;
          }
        }
      });
    });

    // Calculate average notes per active month and format last active month
    Array.from(doctorMap.values()).forEach(doctor => {
      doctor.avgNotes = doctor.monthsActive > 0 ? (doctor.totalNotes / doctor.monthsActive).toFixed(1) : 0;
      
      // Format last active month for display
      if (doctor.lastActiveMonth) {
        const [year, monthNum] = doctor.lastActiveMonth.split('-');
        const date = new Date(parseInt(year), parseInt(monthNum) - 1, 1);
        doctor.lastActiveMonthFormatted = date.toLocaleDateString('en-US', {
          month: 'short',
          year: '2-digit'
        });
      } else {
        doctor.lastActiveMonthFormatted = 'Never';
      }
    });

    return Array.from(doctorMap.values());
  }, [monthlyData, currentMonth]);

  // Filter and sort doctors
  const filteredAndSortedDoctors = useMemo(() => {
    let filtered = [...allDoctorsWithStats];

    // Apply active filter
    if (filterActiveOnly) {
      filtered = filtered.filter(doctor => doctor.currentMonthNotes > 0);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case 'name':
          comparison = a.userName.localeCompare(b.userName);
          break;
        case 'totalNotes':
          comparison = a.totalNotes - b.totalNotes;
          break;
        case 'currentMonthNotes':
          comparison = a.currentMonthNotes - b.currentMonthNotes;
          break;
        case 'avgNotes':
          comparison = parseFloat(a.avgNotes) - parseFloat(b.avgNotes);
          break;
        case 'lastActive':
          // Sort by last active month (most recent first when desc)
          comparison = (a.lastActiveMonth || '0000-00').localeCompare(b.lastActiveMonth || '0000-00');
          break;
        default:
          comparison = a.userName.localeCompare(b.userName);
      }

      return sortOrder === 'desc' ? -comparison : comparison;
    });

    return filtered;
  }, [allDoctorsWithStats, sortBy, sortOrder, filterActiveOnly]);

  // Prepare chart data with doctor activity
  const chartData = useMemo(() => {
    if (!monthlyData?.monthlyOverview) return [];

    return monthlyData.monthlyOverview
      .slice()
      .reverse() // Show chronological order (oldest to newest)
      .map(month => {
        // Fix the date parsing - the month comes as "YYYY-MM" format
        const [year, monthNum] = month.month.split('-');
        const date = new Date(parseInt(year), parseInt(monthNum) - 1, 1); // monthNum - 1 because Date months are 0-based

        const baseData = {
          month: month.month,
          monthLabel: date.toLocaleDateString('en-US', {
            month: 'short',
            year: '2-digit'
          }),
          totalEnrolled: month.totalEnrolledDoctors,
          activeDoctors: month.activeDoctors,
          totalNotes: month.totalNotes,
          activationRate: month.activationRate,
          avgNotesPerDoctor: month.avgNotesPerActiveDoctor
        };

        // Add doctor-specific data
        if (selectedDoctors.size > 0) {
          selectedDoctors.forEach(doctorId => {
            const doctor = allDoctorsWithStats.find(d => d.userId === doctorId);
            const doctorStat = month.doctorStats.find(stat => stat.userId === doctorId);

            if (doctor) {
              baseData[`doctor_${doctorId}`] = doctorStat ? doctorStat.noteCount : 0;
            }
          });
        }

        return baseData;
      });
  }, [monthlyData, selectedDoctors, allDoctorsWithStats]);

  const handleDoctorToggle = (doctorId) => {
    const newSelected = new Set(selectedDoctors);

    if (newSelected.has(doctorId)) {
      newSelected.delete(doctorId);
    } else {
      newSelected.add(doctorId);
    }

    setSelectedDoctors(newSelected);
  };

  const generateDoctorColor = (index) => {
    const colors = [
      '#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#00ff00',
      '#ff00ff', '#00ffff', '#ffff00', '#ff0000', '#0000ff',
      '#800080', '#008000', '#ffa500', '#ffc0cb', '#a52a2a'
    ];
    return colors[index % colors.length];
  };

  const handleQuickSelect = (type) => {
    let doctorsToSelect = [];

    switch (type) {
      case 'top5Total':
        doctorsToSelect = [...filteredAndSortedDoctors]
          .sort((a, b) => b.totalNotes - a.totalNotes)
          .slice(0, 5);
        break;
      case 'top5Current':
        doctorsToSelect = [...filteredAndSortedDoctors]
          .sort((a, b) => b.currentMonthNotes - a.currentMonthNotes)
          .slice(0, 5);
        break;
      case 'activeOnly':
        doctorsToSelect = filteredAndSortedDoctors.filter(d => d.currentMonthNotes > 0);
        break;
      case 'clear':
        doctorsToSelect = [];
        break;
      default:
        doctorsToSelect = [];
    }

    setSelectedDoctors(new Set(doctorsToSelect.map(d => d.userId)));
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-300 rounded-lg shadow-lg">
          <p className="font-semibold text-gray-800">{`${label}`}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }} className="text-sm">
              {`${entry.dataKey === 'totalEnrolled' ? 'Total Enrolled' :
                entry.dataKey === 'activeDoctors' ? 'Active Doctors' :
                  entry.dataKey === 'totalNotes' ? 'Total Notes' :
                    entry.dataKey === 'activationRate' ? 'Activation Rate' :
                      entry.dataKey === 'avgNotesPerDoctor' ? 'Avg Notes/Doctor' :
                        entry.dataKey.startsWith('doctor_') ? allDoctorsWithStats.find(d => d.userId === entry.dataKey.replace('doctor_', ''))?.userName || 'Unknown Doctor' :
                          entry.dataKey}: ${entry.value}${entry.dataKey === 'activationRate' ? '%' : ''}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl shadow-2xl w-[95%] max-w-7xl h-[90%] flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <span className="w-8 h-8 border-2 border-dashed rounded-full animate-spin border-ktx-purple"></span>
            <p className="text-gray-600">Loading monthly overview...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-[95%] max-w-7xl h-[90%] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-ktx-purple">{org} - Monthly Overview</h2>
            <p className="text-gray-600 text-sm mt-1">Activity trends and doctor performance</p>
          </div>

          <div className="flex items-center gap-4">
            {/* Months Back Selector */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">Months:</label>
              <select
                value={monthsBack}
                onChange={(e) => setMonthsBack(parseInt(e.target.value))}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ktx-purple text-sm"
              >
                <option value={1}>1 month</option>
                <option value={3}>3 months</option>
                <option value={6}>6 months</option>
                <option value={12}>12 months</option>
                <option value={18}>18 months</option>
                <option value={24}>24 months</option>
              </select>
            </div>

            {/* Chart Type Selector */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">Chart:</label>
              <select
                value={chartType}
                onChange={(e) => setChartType(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ktx-purple text-sm"
              >
                <option value="line">Line Chart</option>
                <option value="bar">Bar Chart</option>
                <option value="area">Area Chart</option>
                <option value="composed">Combined Chart</option>
              </select>
            </div>

            <button
              className='p-2 hover:bg-gray-100 rounded-full transition-colors'
              onClick={() => setOpenOrgModal(false)}
            >
              <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Doctor Selection Sidebar */}
          <div className="w-80 border-r border-gray-200 flex flex-col">
            <div className="p-4 border-b border-gray-200">
              <h3 className="font-semibold text-gray-800 mb-3">Doctor Activity</h3>

              {/* Sort Controls */}
              <div className="space-y-3 mb-4">
                <div className="flex gap-2">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ktx-purple text-sm"
                  >
                    <option value="name">Sort by Name</option>
                    <option value="totalNotes">Sort by Total Notes</option>
                    <option value="currentMonthNotes">Sort by Current Month</option>
                    <option value="avgNotes">Sort by Avg Notes</option>
                    <option value="lastActive">Sort by Last Active</option>
                  </select>
                  <button
                    onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                    className="px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 transition-colors"
                    title={`Currently: ${sortOrder === 'asc' ? 'Ascending' : 'Descending'}`}
                  >
                    {sortOrder === 'asc' ? '↑' : '↓'}
                  </button>
                </div>

                {/* Active Filter */}
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={filterActiveOnly}
                    onChange={(e) => setFilterActiveOnly(e.target.checked)}
                    className="w-4 h-4 text-ktx-purple focus:ring-ktx-purple border-gray-300 rounded"
                  />
                  Show only active this month
                </label>
              </div>

              {/* Quick Actions */}
              <div className="grid grid-cols-2 gap-2 mb-3">
                <button
                  onClick={() => handleQuickSelect('top5Total')}
                  className="px-3 py-1 text-xs bg-blue-600 text-white rounded-full hover:bg-blue-700"
                >
                  Top 5 Total
                </button>
                <button
                  onClick={() => handleQuickSelect('top5Current')}
                  className="px-3 py-1 text-xs bg-green-600 text-white rounded-full hover:bg-green-700"
                >
                  Top 5 Current
                </button>
                <button
                  onClick={() => handleQuickSelect('activeOnly')}
                  className="px-3 py-1 text-xs bg-ktx-purple text-white rounded-full hover:bg-ktx-navy"
                >
                  All Active
                </button>
                <button
                  onClick={() => handleQuickSelect('clear')}
                  className="px-3 py-1 text-xs bg-gray-500 text-white rounded-full hover:bg-gray-600"
                >
                  Clear All
                </button>
              </div>

              <div className="text-xs text-gray-600 space-y-1">
                <p>Selected: {selectedDoctors.size} / {filteredAndSortedDoctors.length} doctors</p>
                {currentMonth && (
                  <p>Current Month: {currentMonth.month}</p>
                )}
              </div>
            </div>

            {/* Doctor List */}
            <div className="flex-1 overflow-y-auto p-4">
              <div className="space-y-2">
                {filteredAndSortedDoctors.map((doctor, index) => (
                  <label
                    key={doctor.userId}
                    className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedDoctors.has(doctor.userId)}
                      onChange={() => handleDoctorToggle(doctor.userId)}
                      className="w-4 h-4 text-ktx-purple focus:ring-ktx-purple border-gray-300 rounded"
                    />
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: generateDoctorColor(index) }}
                      />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-800 truncate">
                          {doctor.userName}
                        </p>
                        <div className="flex justify-between items-center text-xs text-gray-500">
                          <span className="truncate">{doctor.userEmail}</span>
                        </div>
                        <div className="flex justify-between items-center text-xs mt-1">
                          <span className="text-blue-600 font-medium">
                            Current: {doctor.currentMonthNotes}
                          </span>
                          <span className="text-gray-500">
                            Total: {doctor.totalNotes}
                          </span>
                        </div>
                        <div className="flex justify-between items-center text-xs mt-1">
                          <span className="text-gray-400">
                            Avg: {doctor.avgNotes}/month
                          </span>
                          <span className="text-purple-600 font-medium">
                            Last: {doctor.lastActiveMonthFormatted}
                          </span>
                        </div>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Chart Area */}
          <div className="flex-1 p-6">
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                {chartType === 'line' && (
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="monthLabel" />
                    <YAxis />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Line type="monotone" dataKey="totalEnrolled" stroke="#8884d8" name="Total Enrolled" strokeWidth={2} />
                    <Line type="monotone" dataKey="activeDoctors" stroke="#82ca9d" name="Active Doctors" strokeWidth={2} />
                    <Line type="monotone" dataKey="totalNotes" stroke="#ffc658" name="Total Notes" strokeWidth={2} />

                    {/* Individual Doctor Lines */}
                    {Array.from(selectedDoctors).map((doctorId, index) => {
                      const doctor = allDoctorsWithStats.find(d => d.userId === doctorId);
                      return (
                        <Line
                          key={doctorId}
                          type="monotone"
                          dataKey={`doctor_${doctorId}`}
                          stroke={generateDoctorColor(index)}
                          name={doctor?.userName || `Doctor ${doctorId}`}
                          strokeWidth={1}
                          strokeDasharray="5 5"
                        />
                      );
                    })}
                  </LineChart>
                )}

                {chartType === 'bar' && (
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="monthLabel" />
                    <YAxis />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar dataKey="totalEnrolled" fill="#8884d8" name="Total Enrolled" />
                    <Bar dataKey="activeDoctors" fill="#82ca9d" name="Active Doctors" />
                  </BarChart>
                )}

                {chartType === 'area' && (
                  <AreaChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="monthLabel" />
                    <YAxis />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Area type="monotone" dataKey="totalEnrolled" stackId="1" stroke="#8884d8" fill="#8884d8" name="Total Enrolled" />
                    <Area type="monotone" dataKey="activeDoctors" stackId="1" stroke="#82ca9d" fill="#82ca9d" name="Active Doctors" />
                  </AreaChart>
                )}

                {chartType === 'composed' && (
                  <ComposedChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="monthLabel" />
                    <YAxis yAxisId="left" />
                    <YAxis yAxisId="right" orientation="right" />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar yAxisId="left" dataKey="activeDoctors" fill="#82ca9d" name="Active Doctors" />
                    <Line yAxisId="right" type="monotone" dataKey="activationRate" stroke="#ff7300" name="Activation Rate %" strokeWidth={2} />

                    {/* Individual Doctor Lines */}
                    {Array.from(selectedDoctors).map((doctorId, index) => {
                      const doctor = allDoctorsWithStats.find(d => d.userId === doctorId);
                      return (
                        <Line
                          key={doctorId}
                          yAxisId="left"
                          type="monotone"
                          dataKey={`doctor_${doctorId}`}
                          stroke={generateDoctorColor(index)}
                          name={doctor?.userName || `Doctor ${doctorId}`}
                          strokeWidth={1}
                          strokeDasharray="5 5"
                        />
                      );
                    })}
                  </ComposedChart>
                )}
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="text-gray-400 text-6xl mb-4">📊</div>
                  <p className="text-gray-500 text-lg">No data available</p>
                  <p className="text-gray-400 text-sm">Try selecting a different time range</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}